import React, { useState, useEffect } from "react";
import { Box, VStack, Text, Spinner, Grid, useDisclosure } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import showToast from "hooks/useCustomToast";
import DocModal from "modules/eva/components/DocModal";
import EvaEntityLeftPanel from "modules/eva/components/EvaEntityLeftPanel";
import EvaPromptRightPanel from "modules/eva/containers/EvaPromptRightPanel";
import { DarwinAgent, DarwinTool, PromptProto, SkillProto } from "build-ts/darwin_agent";
import { JinjaEnv } from "build-ts/jinja";
import {
  createNewAgent,
  createSkill,
  getSkillsList,
  getToolsList,
  getSystemPromptDocumentation,
} from "services/EvaServices";
import { DarwinAgentType, DarwinHeader, darwinAgentTypeFromJSON, ReasoningEffortLevel } from "build-ts/common";

interface EvaEntityCreateProps {
  isSkillMode?: boolean;
}

const EvaEntityCreate: React.FC<EvaEntityCreateProps> = ({ isSkillMode = false }) => {
  const navigate = useNavigate();

  const [promptEditor, setPromptEditor] = useState("");
  const [agent, setAgentConfig] = useState<DarwinAgent>({
    header: {
      id: "",
      name: "",
      description: "",
      type: "",
      is_active: false,
    },
    prompts: [],
    tools: [],
    skills: [],
    context_template: "",
  });
  const [skill, setSkillConfig] = useState<SkillProto>({
    header: {
      id: "",
      name: "",
      description: "",
    },
    agentic_name: "",
    prompts: [],
    tools: [],
  });

  const [promptConfig, setPromptConfig] = useState<PromptProto>({
    header: {
      id: "",
      description: "",
      is_active: true,
      version: 1,
    },
    model_config: {
      model_name: "gpt-4o",
      model_provider: "openai",
      temperature: 0,
      reasoning_effort_level: ReasoningEffortLevel.REASONING_NOT_VALID,
    },
    template: promptEditor.trim() || "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [availableTools, setAvailableTools] = useState<DarwinTool[]>([]);
  const [availableSkills, setAvailableSkills] = useState<SkillProto[]>([]);
  const [, setIsSaving] = useState(false);

  const [availableVariables, setAvailableVariables] = useState<JinjaEnv | null>(null);
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const { isOpen: isDocsOpen, onOpen: onDocsOpen, onClose: onDocsClose } = useDisclosure();

  const handleViewDocs = async () => {
    onDocsOpen();
    try {
      setIsLoadingDoc(true);
      const data = await getSystemPromptDocumentation();
      setAvailableVariables(data || null);
    } catch (error) {
      console.error("Error fetching system prompt documentation:", error);
      showToast("Failed to fetch documentation", "", "error");
      setAvailableVariables(null);
    } finally {
      setIsLoadingDoc(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const toolParams = new URLSearchParams({ limit: "100", offset: "0", sort_by: "name", sort_order: "asc" });
        const skillParams = new URLSearchParams({
          limit: "500",
          offset: "0",
          sort_by: "CREATED_DATE",
          sort_order: "ASC",
        });
        const [toolsData, skillsResult] = await Promise.all([
          getToolsList(toolParams),
          getSkillsList(skillParams).catch(() => ({ headers: [] })),
        ]);
        setAvailableTools(toolsData.tools || []);
        setAvailableSkills(
          (skillsResult.headers || []).map((header: DarwinHeader) => ({ header, prompts: [], tools: [] }))
        );
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfigChange = (field: string, value: any) => {
    if (isSkillMode) {
      setSkillConfig(prev => {
        if (field.includes(".")) {
          const [parentField, childField] = field.split(".");
          if (parentField === "header") {
            return { ...prev, header: { ...prev.header, [childField]: value } };
          }
        }
        return { ...prev, [field]: value };
      });
      return;
    }

    setAgentConfig(prev => {
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        if (parentField === "header") {
          return { ...prev, header: { ...prev.header, [childField]: value } };
        }
      }
      return { ...prev, [field]: value };
    });
  };

  const handleToolsChange = (selectedTools: DarwinTool[]) => {
    if (isSkillMode) {
      setSkillConfig(prev => ({ ...prev, tools: selectedTools }));
      return;
    }
    setAgentConfig(prev => ({ ...prev, tools: selectedTools }));
  };

  const handleSkillsChange = (skills: SkillProto[]) => {
    if (!isSkillMode) {
      setAgentConfig(prev => ({ ...prev, skills }));
    }
  };

  const handlePromptConfigChange = (field: string, value: any) => {
    setPromptConfig(prev => {
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        if (parentField === "header") {
          return { ...prev, header: { ...prev.header, [childField]: value } };
        }
        if (parentField === "model_config") {
          return { ...prev, model_config: { ...prev.model_config, [childField]: value } };
        }
      }
      return { ...prev, [field]: value };
    });
  };

  const handlePromptEditorChange = (value: string) => {
    setPromptEditor(value);
    setPromptConfig(prev => ({ ...prev, template: value }));
  };

  const handleSave = async () => {
    if (isSkillMode) {
      if (!skill.header?.name?.trim()) {
        showToast("Validation Error", "Skill name is required", "error");
        return;
      }
      if (!skill.agentic_name?.trim()) {
        showToast("Validation Error", "Agentic name is required", "error");
        return;
      }
    } else {
      if (!agent.header?.name?.trim()) {
        showToast("Validation Error", "Agent name is required", "error");
        return;
      }
      if (!agent.header?.type) {
        showToast("Validation Error", "Agent type is required", "error");
        return;
      }
    }

    setIsSaving(true);
    try {
      const promptPayload: PromptProto = {
        header: {
          id: promptConfig.header?.id || "",
          name: "PROMPT",
          version: promptConfig.header?.version || 1,
          description: promptConfig.header?.description || "",
          is_active: promptConfig.header?.is_active ?? true,
        },
        template: promptEditor.trim() || "",
      };
      if (!isSkillMode) {
        promptPayload.model_config = {
          model_name: promptConfig.model_config?.model_name || "gpt-4o",
          model_provider: promptConfig.model_config?.model_provider || "openai",
          temperature: promptConfig.model_config?.temperature || 0,
          reasoning_effort_level:
            promptConfig.model_config?.reasoning_effort_level || ReasoningEffortLevel.REASONING_NOT_VALID,
        };
      }

      if (isSkillMode) {
        const skillData: SkillProto = {
          header: {
            id: "",
            name: skill.header?.name?.trim() || "",
            description: skill.header?.description?.trim() || "",
          },
          agentic_name: skill.agentic_name?.trim() || "",
          prompts: [promptPayload],
          tools: skill.tools || [],
        };
        const createdSkill = await createSkill(skillData);
        if (createdSkill) {
          showToast("Success", "Skill created successfully", "success");
          navigate(`/eva/skill/${createdSkill.header?.id}`);
        }
      } else {
        const agentData: DarwinAgent = {
          header: {
            id: "",
            name: agent.header?.name?.trim() || "",
            description: agent.header?.description?.trim() || "",
            type: agent.header?.type || "",
            is_active: agent.header?.is_active ?? true,
          },
          prompts: [promptPayload],
          tools: agent.tools || [],
          skills: agent.skills || [],
          context_template: agent.context_template?.trim() || undefined,
        };
        const createdAgent = await createNewAgent(agentData);
        if (createdAgent) {
          showToast("Success", "Agent created successfully", "success");
          navigate(`/eva/${createdAgent.header?.id}`);
        }
      }
    } catch (error: any) {
      showToast(
        isSkillMode ? "Failed to create skill" : "Failed to create agent",
        error?.message || "Please try again.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(isSkillMode ? "/eva/skills" : "/eva");
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading data...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box bg="gray.100" p={2} height="100vh" overflow="hidden">
      <Grid height="100%" width="100%" templateColumns="minmax(0, 25%) minmax(0, 75%)" gap={1}>
        <EvaEntityLeftPanel
          {...(isSkillMode ? { skill } : { agent })}
          availableTools={availableTools}
          availableSkills={availableSkills}
          onConfigChange={handleConfigChange}
          onToolsChange={handleToolsChange}
          onSkillsChange={handleSkillsChange}
          isCreateMode={true}
          onCancel={handleCancel}
          onSave={handleSave}
          onViewDocs={handleViewDocs}
        />

        <EvaPromptRightPanel
          {...(isSkillMode ? { skill } : { agent })}
          promptConfig={promptConfig}
          promptEditor={promptEditor}
          onPromptEditorChange={handlePromptEditorChange}
          onPromptConfigChange={handlePromptConfigChange}
          isCreateMode={true}
          onViewDocs={handleViewDocs}
        />
      </Grid>

      <DocModal
        isDocsOpen={isDocsOpen}
        onDocsClose={onDocsClose}
        isLoadingDoc={isLoadingDoc}
        availableVariables={availableVariables}
        agentType={
          isSkillMode
            ? DarwinAgentType.ANALYST_CHAT_AGENT
            : agent.header?.type
              ? darwinAgentTypeFromJSON(agent.header.type)
              : undefined
        }
        isSkillMode={isSkillMode}
      />
    </Box>
  );
};

export default EvaEntityCreate;
